/**
 * BugReportModal — Simple modal for submitting bug reports or feature requests.
 *
 * Used in both OfficeLayout and TechLayout.
 * Up to 3 images can be attached, either taken with the camera or picked
 * from the photo library. Two inputs, as on the tech job card: the camera
 * one carries capture="environment", which on iOS opens the camera ONLY —
 * so a screenshot (already in the library) needs the second input, which
 * omits capture and lets the OS show the library / file picker.
 */

import { getTenantIdSync } from '@/lib/tenant';
import { useState, useRef } from 'react';
import { X, Bug, Lightbulb, Send, Loader, Camera, ImagePlus, Trash2 } from 'lucide-react';
import { submitBugReport, ReportType } from '@/services/bug-report.service';
import { usageBreadcrumbs } from '@/lib/usage/UsageTracker';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/firebase';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Current page path for context */
  currentPage: string;
}

export function BugReportModal({ isOpen, onClose, currentPage }: BugReportModalProps) {
  const [type, setType] = useState<ReportType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<{ file: File; preview: string }[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const userId = localStorage.getItem('user_id') || '';
  const userName = localStorage.getItem('logged_in_user') || 'Unknown';
  const userRole = localStorage.getItem('user_role') || 'office';
  const tenantId = getTenantIdSync();

  const handleAddPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newPhotos: { file: File; preview: string }[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024) {
        newPhotos.push({ file, preview: URL.createObjectURL(file) });
      }
    }
    setPhotos((prev) => [...prev, ...newPhotos].slice(0, 3)); // max 3
    // Reset whichever input fired so the same file can be re-selected
    e.target.value = '';
  };

  const handleRemovePhoto = (index: number) => {
    setPhotos((prev) => {
      const removed = prev[index];
      URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadPhotos = async (): Promise<string[]> => {
    if (photos.length === 0) return [];

    const urls: string[] = [];
    for (const photo of photos) {
      const timestamp = Date.now();
      const safeName = photo.file.name.replace(/[^a-zA-Z0-9._-]/g, '_').substring(0, 80);
      const path = `bug_reports/${tenantId}/${timestamp}_${safeName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, photo.file, { contentType: photo.file.type || 'image/jpeg' });
      const url = await getDownloadURL(storageRef);
      urls.push(url);
    }
    return urls;
  };

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    setError(false);
    try {
      // Upload photos first
      const photoUrls = await uploadPhotos();
      // The last things they did in Theo (no values, no customer data) — the
      // pipeline's understand phase reads this trail before the description.
      const trail = await usageBreadcrumbs().catch(() => null);

      await submitBugReport({
        tenantId,
        type,
        title: title.trim(),
        description: description.trim(),
        page: currentPage,
        reportedBy: userId,
        reportedByName: userName,
        reportedByRole: userRole,
        ...(photoUrls.length > 0 ? { photoUrls } : {}),
        ...(trail && trail.breadcrumbs.length > 0 ? { breadcrumbs: trail.breadcrumbs, routePattern: trail.routePattern } : {}),
      });
      setSubmitted(true);
      // Cleanup previews
      photos.forEach((p) => URL.revokeObjectURL(p.preview));
      setTimeout(() => {
        setSubmitted(false);
        setTitle('');
        setDescription('');
        setPhotos([]);
        setType('bug');
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Failed to submit report:', err);
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Report an Issue</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {submitted ? (
          <div className="px-5 py-12 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <Send className="w-6 h-6 text-emerald-600" />
            </div>
            <p className="text-lg font-medium text-gray-900 dark:text-white">Thanks!</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Your report has been submitted.</p>
          </div>
        ) : (
          <div className="px-5 py-4 space-y-4">
            {/* Type Picker */}
            <div className="flex gap-2">
              <button
                onClick={() => setType('bug')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  type === 'bug'
                    ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-2 border-red-300 dark:border-red-700'
                    : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-2 border-transparent hover:border-gray-200 dark:hover:border-gray-600'
                }`}
              >
                <Bug className="w-4 h-4" />
                Bug
              </button>
              <button
                onClick={() => setType('feature')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  type === 'feature'
                    ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-2 border-amber-300 dark:border-amber-700'
                    : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-2 border-transparent hover:border-gray-200 dark:hover:border-gray-600'
                }`}
              >
                <Lightbulb className="w-4 h-4" />
                Feature Idea
              </button>
            </div>

            {/* Title */}
            <div>
              <input
                type="text"
                placeholder={type === 'bug' ? "What's broken?" : "What would you like?"}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                autoFocus
              />
            </div>

            {/* Description */}
            <div>
              <textarea
                placeholder="Any extra details... (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
              />
            </div>

            {/* Photo Attachments */}
            <div>
              {/* Photo previews */}
              {photos.length > 0 && (
                <div className="flex gap-2 mb-2">
                  {photos.map((photo, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                      <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => handleRemovePhoto(i)}
                        aria-label="Remove screenshot"
                        className="absolute top-0 right-0 p-0.5 bg-black/60 rounded-bl-lg text-white"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add photo buttons — camera or library */}
              {photos.length < 3 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => cameraInputRef.current?.click()}
                    className="flex-1 min-h-[44px] flex items-center justify-center gap-2 px-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-400 hover:text-gray-600 active:scale-[0.98] transition-all text-xs"
                  >
                    <Camera className="w-4 h-4" />
                    Take photo
                  </button>
                  <button
                    onClick={() => libraryInputRef.current?.click()}
                    className="flex-1 min-h-[44px] flex items-center justify-center gap-2 px-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-400 hover:text-gray-600 active:scale-[0.98] transition-all text-xs"
                  >
                    <ImagePlus className="w-4 h-4" />
                    From library
                  </button>
                  {photos.length > 0 && (
                    <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">{photos.length}/3</span>
                  )}
                </div>
              )}

              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleAddPhoto}
                className="hidden"
              />
              <input
                ref={libraryInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleAddPhoto}
                className="hidden"
              />
            </div>

            {/* Context info */}
            <p className="text-[11px] text-gray-400 dark:text-gray-500">
              Page: {currentPage} &middot; By: {userName}
            </p>

            {/* Error message */}
            {error && (
              <p className="text-sm text-red-600 dark:text-red-400">Failed to submit. Please try again.</p>
            )}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={!title.trim() || submitting}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white font-medium text-sm transition-colors"
            >
              {submitting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {submitting ? (photos.length > 0 ? 'Uploading...' : 'Submitting...') : 'Submit'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
